"use client";

import { useState, useEffect } from "react";
import FiltersSection from "./FiltersSection";
import DisputeModal from "./DisputeModal";
import ActionLogsModal from "./ActionLogsModal";
import SuccessModal from "./SuccessModal";
import OTPModal from "./OTPModal";
import styles from "./ShiftJobsTable.module.css";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useDispatch, useSelector } from "react-redux";
import {
  listBranches,
  listShiftLogs,
  exportListShiftLogs,
} from "../Redux/actionCreator";
import { getProfessionsList } from "../../common/Redux/actionCreator";
import { pageChanged, pageLimit } from "../Redux/reducer";
import { Link, useLocation, useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import { get_Token } from "util/Helper";
import axiosInstance from "../../../axios";
import moment from "moment";
import { toast } from "react-toastify";

const getApiStatus = (status, reverse = false) => {
  if (reverse) {
    // Convert from API status to display status
    switch (status) {
      case "APPROVED":
        return "Approved";
      case "PENDING_APPROVAL":
        return "Pending";
      case "DISPUTED":
        return "Disputed";
      case "RESOLVED":
        return "Approved";
      default:
        return status;
    }
  } else {
    // Convert from display status to API status
    switch (status) {
      case "Approved":
        return "APPROVED";
      case "Pending":
        return "PENDING_APPROVAL";
      case "Disputed":
        return "DISPUTED";
      case "Resolved":
        return "RESOLVED";
      case "All Status":
      default:
        return "";
    }
  }
};

const formatOflStatus = (status) => {
  if (!status) return null;
  const normalized = String(status).trim().replace(/-/g, "_").toUpperCase();
  const statusMap = {
    EARLY: "Early",
    LATE: "Late",
    COMPLETED_ON_TIME: "Completed On Time",
    ON_TIME: "On time",
    EARLY_LATE: "Early/Late shift completion",
    OUT_OF_LOCATION: "Out of location",
  };
  return statusMap[normalized] || status;
};

const formatGpsPingScore = (score) => {
  if (!score) return "Not Identified";
  const scoreMap = {
    HIGH: "High",
    MEDIUM: "Medium",
    LOW: "Low",
    NOT_IDENTIFIED: "Not Identified",
  };
  return scoreMap[score] || score;
};

// Check-in proximity only ever resolves to High or Not Identified.
const formatCheckInProximityScore = (score) => {
  if (!score) return "Not Identified";
  const normalized = String(score).trim().replace(/-/g, "_").toUpperCase();
  return normalized === "HIGH" ? "High" : "Not Identified";
};

// Locked API field names (same APIs as Auto Check-Out):
// Response: isAutoCheckin, checkInProximityScore, OFLCheckInTime,
//           OFLCheckInStatus, checkOutProximityScore, locationPings
// Request:  autoCheckinOnly (alongside existing autoCheckoutOnly)
const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null);

const PING_SCORE_LABELS = {
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
  NOT_IDENTIFIED: "Not Identified",
};

const normalizeLocationPings = (shift) => {
  // console.log("shift11", shift);
  const raw = shift?.checkInLivePings;
  if (!Array.isArray(raw)) return [];

  return raw
    .filter((ping) => ping && ping.capturedAt) // Filter out dummy records without actual pings
    .map((ping) => {
      const rawScore = ping?.proximityScore || ping?.gpsScore;
      const normalizedScore = rawScore
        ? String(rawScore).trim().replace(/-/g, "_").toUpperCase()
        : "NOT_IDENTIFIED";
      const distance = ping?.distanceInMeters ?? ping?.distanceFromHospital;
      const nData = {
        timestamp: ping?.capturedAt ?? null,

        score: PING_SCORE_LABELS[normalizedScore] || "Not Identified",

        distance:
          distance === undefined || distance === null ? null : Number(distance),

        source: ping?.pingType || null,

        // optional, but useful
        latitude: ping?.latitude ?? null,
        longitude: ping?.longitude ?? null,
        hour: ping?.hour ?? null,
      };
      // console.log("nData", nData);
      return nData;
    // return {
    //   timestamp: ping?.capturedAt ?? null,

    //   score: PING_SCORE_LABELS[normalizedScore] || "Not Identified",

    //   distance:
    //     distance === undefined || distance === null ? null : Number(distance),

    //   source: ping?.pingType || null,

    //   // optional, but useful
    //   latitude: ping?.latitude ?? null,
    //   longitude: ping?.longitude ?? null,
    //   hour: ping?.hour ?? null,
    // };
    // return {
    //   timestamp: ping?.pingAt ?? null,
    //   score: PING_SCORE_LABELS[normalizedScore] || "Not Identified",
    //   distance:
    //     distance === undefined || distance === null ? null : Number(distance),
    //   source: ping?.source || null,
    // };
  });
};


const COLUMN_VISIBILITY_STORAGE_KEY = "shiftLogsHiddenColumns";

// Worked examples for the Auto Check-In explainer. Shift starts 8:00 PM and the
// phone is polled every 20 min from 7:40 PM.
const AUTO_CHECKIN_CASES = [
  {
    id: 1,
    pings: [
      { time: "7:40", label: "High", high: true, mark: "confirm" },
      { time: "7:50", label: "High", high: true, mark: "confirm" },
    ],
    result: "7:50 PM",
    note: "7:40 rounds up to shift start",
  },
  {
    id: 2,
    pings: [
      { time: "7:40", label: "Low" },
      { time: "7:50", label: "Low" },
      { time: "8:00", label: "High", high: true, mark: "confirm" },
      { time: "8:10", label: "High", high: true, mark: "confirm" },
    ],
    result: "8:00 PM",
  },
  {
    id: 3,
    pings: [
      { time: "7:40", label: "Medium" },
      { time: "7:50", label: "Low" },
      { time: "8:00", label: "Low" },
      { time: "8:10", label: "Low" },
      { time: "8:20", label: "High", high: true, mark: "confirm" },
      { time: "8:30", label: "High", high: true, mark: "confirm" },
    ],
    result: "8:20 PM",
  },
  {
    id: 4,
    pings: [
      { time: "7:40", label: "Low" },
      { time: "7:50", label: "Low" },
      { time: "8:00", label: "High", high: true, mark: "isolated" },
      { time: "8:10", label: "Medium" },
      { time: "8:20", label: "Low" },
      { time: "8:30", label: "Low" },
      { time: "8:40", label: "High", high: true, mark: "confirm" },
      { time: "8:50", label: "High", high: true, mark: "confirm" },
    ],
    result: "8:40 PM",
  },
  {
    id: 5,
    pings: [
      { time: "7:40", label: "Medium" },
      { time: "7:50", label: "Low" },
      { time: "8:00", label: "High", high: true, mark: "isolated" },
      { time: "8:10", label: "Low" },
      { time: "8:20", label: "Low" },
      { time: "8:30", label: "Low" },
      { time: "8:40", label: "Medium" },
      { time: "8:50", label: "Medium" },
      { time: "9:00", label: "High", high: true, mark: "confirm" },
      { time: "9:10", label: "High", high: true, mark: "confirm" },
    ],
    result: "9:00 PM",
  },
];

const CHECK_IN_PROXIMITY_HEADER_HINT =
  "High or Not Identified only. High → Auto Check-In Yes. Not Identified → Auto Check-In No, OFL Check-In Time used as fallback if professional self-reports";

const AutoCheckInCases = () => (
  <>
    {AUTO_CHECKIN_CASES.map((testCase) => (
      <div key={testCase.id} className={styles.caseBlock}>
        <div className={styles.caseHead}>Case {testCase.id}</div>
        <div className={styles.pingTimeline}>
          {testCase.pings.map((ping, index) => (
            <div
              key={`${testCase.id}-${index}`}
              className={[
                styles.pingChip,
                ping.high ? styles.pingChipHigh : "",
                ping.mark === "confirm" ? styles.pingChipConfirm : "",
                ping.mark === "isolated" ? styles.pingChipIsolated : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <span className={styles.pingChipTime}>{ping.time}</span>
              <span className={styles.pingChipType}>{ping.label}</span>
            </div>
          ))}
        </div>
        <div className={styles.caseResult}>
          Check-in saved → <b>{testCase.result}</b>
          {testCase.note ? <i> ({testCase.note})</i> : null}
        </div>
      </div>
    ))}
  </>
);

const INFO_CONTENT = {
  checkIn: {
    title: "How Check-In Proximity Score works",
    sub: "In simple points",
    body: (
      <>
        <ul>
          <li>Checks the professional&apos;s phone location every 10 min.</li>
          <li>
            Starts 20 min before shift start, stops 120 min after shift start.
          </li>
          <li>
            Only runs for shifts that need auto check-in — skips camp duty and
            shifts already checked in by hand.
          </li>
          <li>
            <b>High</b> = phone is very close to hospital → good sign.
          </li>
          <li>
            <b>Not Identified</b> = no location data received.
          </li>
          <li>
            One High reading is not enough. Needs{" "}
            <b>2 High readings in a row</b>, 10 min apart, to confirm check-in.
          </li>
          <li>
            Check-in time saved = time of the <b>first</b> of those 2 High
            readings.
          </li>
          <li>
            No confirmed pair within 120 min of shift start → goes to{" "}
            <b>Action Required</b>, professional submits check-in manually.
          </li>
        </ul>
        <div className={styles.infoExample}>
          <b>Example</b>
          Shift starts 9:00 AM. High at 8:40 AM + High at 8:50 AM (back-to-back)
          → check-in confirmed, time saved = 8:40 AM.
        </div>
      </>
    ),
  },
  checkOut: {
    title: "How Check-Out Proximity Score works",
    sub: "In simple points",
    body: (
      <>
        <ul>
          <li>
            Same idea as check-in, but flipped: being <b>far</b> from hospital
            is the good sign here.
          </li>
          <li>
            <b>High</b> = more than 200m away → strong sign the professional has
            left.
          </li>
          <li>
            <b>Medium</b> = 50–200m away.
          </li>
          <li>
            <b>Low</b> = less than 50m away → still on/near hospital premises.
          </li>
          <li>
            <b>Not Identified</b> = no location data received.
          </li>
          <li>
            Why flipped: if the phone is far from hospital, system trusts the
            professional actually left — safe to auto check them out for audit.
          </li>
        </ul>
        <div className={styles.infoExample}>
          <b>Example</b>
          Phone shows High (&gt;200m away) after shift end → treated as a good
          signal to auto check-out.
        </div>
      </>
    ),
  },
  autoCheckIn: {
    title: "How Auto Check-In Works",
    sub: "",
    body: (
      <>
        <ul>
          <li>
            We pay the professional based on when the app actually confirms
            check-in — not just the shift&apos;s start time.
          </li>
          <li>
            The app only confirms check-in after it sees two good signals in a
            row, 10 minutes apart.
          </li>
          <li>
            The check-in time we save is from the <b>first</b> of those two
            signals.
          </li>
          <li>
            If that first signal happens before the shift was due to start, we
            still use the shift start time — no extra pay for arriving early.
          </li>
          <li>
            One good signal alone means nothing. It only counts once a second
            one follows right after.
          </li>
        </ul>
        <p className={styles.infoLead}>
          <b>5 worked examples</b> — shift starts 8:00 PM, phone checked every
          10 min from 7:40 PM:
        </p>
        <AutoCheckInCases />
        <p className={styles.infoFootnote}>
          Circled pings = the confirming pair. Dashed = a good signal that
          didn&apos;t get a partner right after, so it didn&apos;t count.
        </p>
      </>
    ),
  },
};

const ShiftJobsTable = () => {
  const dispatch = useDispatch();
  const { shiftlog_Job_Data, branches, export_shiftlog_Job_Data } = useSelector(
    (state) => state.job,
  );

  const loading = shiftlog_Job_Data.shiftlog_Job_Data_loading;
  let shiftLogs = shiftlog_Job_Data.shiftlog_Job_Data_info;
  // console.log("shiftLogs data", shiftLogs);

  let shiftLogExportData =
    export_shiftlog_Job_Data.export_shiftlog_Job_Data_info;
  //  console.log("shiftLogExportData", shiftLogExportData);

  const { ProfessionsData } = useSelector((state) => state.common);
  const professionsData = ProfessionsData?.professionsInfo;

  const { adminData } = useSelector((state) => ({
    adminData: state?.auth?.adminData,
  }));

  const totalCount = shiftlog_Job_Data?.total;
  const currentPage1 = useSelector((state) => state.job.currentPage);
  const pageSize1 = useSelector((state) => state.job.pageSize);

  const [shifts, setShifts] = useState([]);
  const [filteredShifts, setFilteredShifts] = useState([]);
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [successType, setSuccessType] = useState("single");
  const [currentShift, setCurrentShift] = useState(null);
  const [filters, setFilters] = useState({
    branch: "Branch",
    status: "All Status",
    profession: "Profession",
    autoCheckOut: "",
    startDate: null,
    endDate: null,
    searchKey: "Search",
    autoCheckoutOnly: false,
    autoCheckinOnly: false,
  });

  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpType, setOtpType] = useState("");
  const [pendingDisputeData, setPendingDisputeData] = useState(null);
  const [pendingBulkApproveIds, setPendingBulkApproveIds] = useState([]); // New state for bulk approval
  // const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [branchOptions, setBranchOptions] = useState(["Branch"]);
  const [verifyToken, setVerifyToken] = useState(null);
  const [originalShifts, setOriginalShifts] = useState([]); // New state to store original shifts
  const [loadingExport, setLoadingExport] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [showBulkApproveModal, setShowBulkApproveModal] = useState(false);
   const [pingsLiveShift, setPingsLiveShift] = useState(null);
   const [showColumnMenu, setShowColumnMenu] = useState(false);
   const [hiddenColumns, setHiddenColumns] = useState([]);
   const [columnSearch, setColumnSearch] = useState("");
   const [pingsShift, setPingsShift] = useState(null);
   const [infoKey, setInfoKey] = useState(null);

  // console.log("professionsData", professionsData);
  const professionOptions = [];
  professionsData.map((ele) => {
    let obj = {
      key: ele._id,
      value: ele.name,
    };

    //  console.log("obj", obj);

    professionOptions.push(obj);
  });

  // Fetch branches on component mount
  useEffect(() => {
    dispatch(listBranches());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getProfessionsList());
  }, []);

  useEffect(() => {
    // console.log("Branches from Redux:", branches);
    // console.log("Branches data:", branches?.data?.docs);

    if (branches?.data?.docs?.length > 0) {
      // console.log("Mapping branches:", branches.data.docs);
      const options = [
        "Branch",
        ...branches.data.docs.map(
          (branch) => branch.jobLocation?.branchName || "Unnamed Branch",
        ),
      ];
      // console.log("Branch options:", options);
      setBranchOptions(options);
    }
  }, [branches?.data]);

  useEffect(() => {
    // Dispatch the action to fetch shift logs with mapped status

    const bothDatesSelected =
      filters.startDate && filters.endDate ? true : false;

    if (!bothDatesSelected && (filters.startDate || filters.endDate)) return;

    const branchForPayload =
      filters.branch && filters.branch !== "Branch" ? filters.branch : "";
    // console.log("branchForPayload", branchForPayload);

    const professionForPayload =
      filters.profession && filters.profession !== "Profession"
        ? filters.profession
        : "";

    // ✅ Convert to YYYY-MM-DD format
    const formattedStartDate = filters.startDate
      ? new Date(filters.startDate).toISOString().split("T")[0]
      : "";
    const formattedEndDate = filters.endDate
      ? new Date(filters.endDate).toISOString().split("T")[0]
      : "";

    const searchKeyForPayload =
      filters.searchKey && filters.searchKey !== "Search"
        ? filters.searchKey.trim()
        : "";
    const autoCheckOutForPayload = filters.autoCheckOut === "Yes" ? true : "";

    // ✅ Common filter payload
    const basePayload = {
      approvedStatus: getApiStatus(filters.status),
      filterByHospitalBranch: branchForPayload,
      filterByProfession: professionForPayload,
      filterByAutoCheckOut: autoCheckOutForPayload,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      search: searchKeyForPayload,
      autoCheckoutOnly: filters.autoCheckoutOnly,
      autoCheckinOnly: filters.autoCheckinOnly,
    };

    // ✅ Listing API (with pagination)
    dispatch(
      listShiftLogs({
        ...basePayload,
        currentPage: currentPage1,
        pageSize: pageSize1,
      }),
    );
    // ✅ Export API (without pagination)
    dispatch(exportListShiftLogs(basePayload));

    // dispatch(
    //   listShiftLogs({
    //     approvedStatus: getApiStatus(filters.status),
    //     filterByHospitalBranch: branchForPayload,
    //     filterByProfession: professionForPayload,
    //     startDate: formattedStartDate,
    //     endDate: formattedEndDate,
    //     currentPage: currentPage1,
    //     pageSize: pageSize1,
    //   })
    // );
  }, [
    dispatch,
    filters.status,
    filters.branch,
    filters.profession,
    filters.startDate,
    filters.endDate,
    filters.searchKey,
    filters.autoCheckOut,
    currentPage1,
    pageSize1,
    filters.autoCheckoutOnly,
    filters.autoCheckinOnly,
  ]);
  useEffect(() => {
    console.log("shiftLogs full object:", shiftLogs);

    if (shiftLogs) {
      // console.log("shiftLogs.docs:", shiftLogs);

      const formattedShifts = shiftLogs.map((shift) => ({
        id: shift.code,
        hospitalBranch: shift.branch || "N/A",
        specialization: shift.specialization || "N/A",
        professionalName: shift.userName || "N/A",
        profession: shift.profession || "N/A",
        // professionalNumber: shift.professionalNumber || 'N/A',
        // rename
        shiftStartDateTime: shift.startDateTime
          ? dayjs(Number(shift?.startDateTime)).format("MMM D, YYYY, h:mm A")
          : "N/A",
        shiftEndDateTime: shift.endDateTime
          ? dayjs(Number(shift?.endDateTime)).format("MMM D, YYYY, h:mm A")
          : "N/A",
        in: shift.in
          ? dayjs(Number(shift?.in)).format("MMM D, YYYY, h:mm A")
          : "N/A",
        out: shift.out
          ? dayjs(Number(shift?.out)).format("MMM D, YYYY, h:mm A")
          : "N/A",
        totalCost: shift.hospitalCost
          ? Number(shift.hospitalCost).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : "0",
        status: getApiStatus(shift.approved, true) || "Pending",
        logs: shift.logs || [],
        approvedAt: shift.approvedAt || null,
        disputedReason: shift.disputedReason || null,
        disputedComment: shift.disputedComment || null,
        userId: shift.user || null,
        jobId: shift.id || null,
        timeLineStatus: shift.approved || null,
        disputeAt: shift.disputedCreatedAt || null,
        resolvedAt: shift.resolvedApprovedAt || null,
        department: shift.department || null,
        campDuty: shift.campDuty === true ? "Yes" : "No",
        approveAt: shift.approvedAt
          ? shift.approvedAt
          : shift.resolvedApprovedAt,
        approveBy: shift.approvedByName,
        autoCheckOut: shift.isAutoCheckout,
        // Renamed from GPSPingScore — keep legacy fallback until backend rename ships
        checkOutProximityScore: formatGpsPingScore(
          firstDefined(shift.checkOutProximityScore, shift.GPSPingScore),
        ),
        oflCheckOutTime: shift.OFLCheckOutTime,
        // Renamed from OFLStatus — keep legacy fallback until backend rename ships
        oflCheckInStatus: formatOflStatus(
          firstDefined(shift.OFLCheckInStatus, shift.OFLStatus),
        ),
        // Auto Check-In details come nested in `AutoCheckInDetails` from listShiftLogs.
        // Keep a fallback to potential future flat keys.
        autoCheckIn:
          firstDefined(
            shift.AutoCheckInDetails?.isAutoCheckIn,
            shift.isAutoCheckin,
            shift.isAutoCheckIn,
          ) === true,
        checkInProximityScore: formatCheckInProximityScore(
          firstDefined(
            shift.AutoCheckInDetails?.GPSPingScore,
            shift.checkInProximityScore,
          ),
        ),
        oflCheckInTime:
          firstDefined(
            shift.AutoCheckInDetails?.OFLCheckInTime,
            shift.OFLCheckInTime,
            shift.oflCheckInTime,
          ) ?? null,
        oflCheckInStatus: formatOflStatus(
          firstDefined(
            shift.AutoCheckInDetails?.OFLStatus,
            shift.OFLCheckInStatus,
            shift.oflCheckInStatus,
          ),
        ),
        locationPings: normalizeLocationPings(shift),
        actualJobHours: shift.actualJobHours,
        phoneNumber: shift.phoneNumber || "N/A",
        panNumber: shift.pan || "N/A",
        hospitalCity: shift.hospitalCity || "N/A",
      }));


      setOriginalShifts(formattedShifts);
      setShifts(formattedShifts);
    }
  }, [shiftLogs]); // Remove .data from the dependency

  useEffect(() => {
    if (originalShifts.length === 0) return;

    // console.log("Current filters:", filters);
    // console.log("Original shifts count:", originalShifts.length);

    let filtered = [...originalShifts];

    // Apply branch filter if needed
    if (filters.branch && filters.branch !== "Branch") {
      // console.log("Applying branch filter:", filters.branch);
      filtered = filtered.filter((shift) => {
        const match = shift.hospitalBranch === filters.branch;
        // console.log(
        //   `Shift ${shift.id}: ${shift.hospitalBranch} ${
        //     match ? "matches" : "does not match"
        //   } ${filters.branch}`
        // );
        return match;
      });
    }

    // Apply status filter if needed
    if (filters.status && filters.status !== "All Status") {
      // console.log("Applying status filter:", filters.status);
      filtered = filtered.filter((shift) => {
        const match = shift.status === filters.status;
        // console.log(
        //   `Shift ${shift.id}: Status ${shift.status} ${
        //     match ? "matches" : "does not match"
        //   } ${filters.status}`
        // );
        return match;
      });
    }

    // Log the actual filtered items for verification
    // console.log("Filtered shifts:", filtered);
    // console.log("Final filtered count:", filtered.length);

    // Use a callback to ensure we're working with the latest state
    setFilteredShifts((prevFilteredShifts) => {
      // console.log("Setting filtered shifts count:", filtered.length);
      return filtered;
    });

    setSelectedShifts([]);
  }, [originalShifts, filters]);

  useEffect(() => {
    if (!showColumnMenu) return undefined;
    const closeOnOutsideClick = () => setShowColumnMenu(false);
    document.addEventListener("click", closeOnOutsideClick);
    return () => document.removeEventListener("click", closeOnOutsideClick);
  }, [showColumnMenu]);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      setPingsShift(null);
      setInfoKey(null);
      setShowColumnMenu(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, []);

  function formattDate() {
    const now = new Date();

    const day = String(now.getDate()).padStart(2, "0");

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const month = monthNames[now.getMonth()];
    const year = now.getFullYear();

    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");

    const formattedDate = `${day} ${month} ${year}_${hours}_${minutes}_${seconds}`;

    return formattedDate;
  }
  console.log("adminData", adminData);
  const handleDownloadData = async () => {
    const hasExportPermission =
      adminData?.isSuperAdmin ||
      adminData?.permissions?.some(
        (item) =>
          item.module === "job_shiftLogs" && item.actions.includes("export"),
      );

    console.log("hasExportPermission", hasExportPermission);

    if (!hasExportPermission) {
      toast.error("Access Denied: You don't have permission to export data");
      return;
    }
    setLoadingExport(true); // Start loading

    try {
      let csvData = "";
      csvData = shiftLogExportData;

      console.log("csvData", csvData);

      const csvContent = new Blob([csvData], {
        type: "text/csv;charset=utf-8",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(csvContent);
      link.download = `Job_Shiftlogs-${formattDate()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error fetching or downloading data:", error);
    } finally {
      setLoadingExport(false); // Stop loading
    }
  };

  const handleResetFilters = () => {
    setFilters({
      branch: "Branch",
      status: "All Status",
      profession: "Profession",
      autoCheckOut: "",
      startDate: null,
      endDate: null,
      searchKey: "Search",
      autoCheckoutOnly: false,
      autoCheckinOnly: false,
    });
    setSearchInput("");
    dispatch(pageChanged(0));
  };

  const currentItems = filteredShifts;
  const totalPages = Math.ceil(totalCount / pageSize1);
  const pageStart =
    filteredShifts.length === 0 ? 0 : currentPage1 * pageSize1 + 1;
  const pageEnd =
    filteredShifts.length === 0 ? 0 : pageStart + filteredShifts.length - 1;

  const PaginationControls = () => {
    return (
      <div className={styles.paginationContainer}>
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <span
            style={{
              color: "#666",
              fontSize: "14px",
            }}
          >
            Rows per page:
          </span>
          <select
            value={pageSize1}
            onChange={(e) => {
              dispatch(pageLimit(Number(e.target.value)));
              dispatch(pageChanged(0));
            }}
            style={{
              padding: "5px 10px",
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              cursor: "pointer",
              backgroundColor: "#fff",
              marginRight: "15px",
              fontSize: "14px",
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          <span
            style={{
              color: "#666",
              fontSize: "14px",
            }}
          >
            {`${pageStart} - ${pageEnd} of ${totalCount}`}
          </span>
        </div>

        <div
          style={{
            display: "flex",
            gap: "5px",
            alignItems: "center",
            marginLeft: 5,
          }}
        >
          <button
            onClick={() => dispatch(pageChanged(currentPage1 - 1))}
            disabled={currentPage1 === 0}
            style={{
              padding: "5px 10px",
              border: "1px solid #e0e0e0",
              background: currentPage1 === 0 ? "#f5f5f5" : "#fff",
              borderRadius: "4px",
              cursor: currentPage1 === 0 ? "not-allowed" : "pointer",
              opacity: currentPage1 === 0 ? 0.6 : 1,
            }}
          >
            &lt;
          </button>

          {/* <span style={{ color: '#666', fontSize: '14px', padding: '0 10px' }}>
            Page {currentPage} of {totalPages}
          </span> */}

          <button
            onClick={() => dispatch(pageChanged(currentPage1 + 1))}
            disabled={currentPage1 === totalPages - 1}
            style={{
              padding: "5px 10px",
              border: "1px solid #e0e0e0",
              background: currentPage1 === totalPages - 1 ? "#f5f5f5" : "#fff",
              borderRadius: "4px",
              cursor:
                currentPage1 === totalPages - 1 ? "not-allowed" : "pointer",
              opacity: currentPage1 === totalPages - 1 ? 0.6 : 1,
            }}
          >
            &gt;
          </button>
        </div>
      </div>
    );
  };

  // if (loading) {
  //   return (
  //     <DashboardLayout>
  //       <DashboardNavbar />
  //       <div className={styles.loadingContainer}>
  //         <div className={styles.spinner}></div>
  //         <p>Loading shift logs...</p>
  //       </div>
  //     </DashboardLayout>
  //   );
  // }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div className={styles.errorContainer}>
          <p className={styles.errorText}>{error}</p>
          <button
            className={styles.retryButton}
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const handleApprove = async (shiftId) => {
    try {
      const shiftToApprove = shifts.find((shift) => shift.id === shiftId);
      if (!shiftToApprove) return;
      // console.log("shiftToApprove==", shiftToApprove);

      // Prepare shift data with UPPERCASE field names
      const shiftData = {
        PROFESSIONAL_NAME: shiftToApprove.professionalName || "",
        JOB_ID: shiftToApprove.id || "",
        BRANCH_NAME: shiftToApprove.hospitalBranch || "",
        SHIFT_START_DATE_TIME: shiftToApprove.shiftStartDateTime || "",
        SHIFT_END_DATE_TIME: shiftToApprove.shiftEndDateTime || "",
        CURRENT_STATUS: "PENDING_APPROVAL",
      };

      // Call OTP API with the shift in an array
      let criteria = {
        REQUESTED_STATUS: "APPROVED",
        shifts: [shiftData],
      };

      // Call OTP API with the shift in an array
      const response = await axiosInstance.post(`/common/sendOtp`, criteria);

      // const response = await fetch('https://jobizo-admin.jobizo.com/common/sendOtp', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'Authorization': `Bearer ${get_Token()}`
      //   },
      //   body: JSON.stringify({
      //     REQUESTED_STATUS: 'APPROVED',
      //     shifts: [shiftData],
      //   }),
      // });

      if (response) {
        // console.log("response==", response);
        const responseData = await response;
        const verifyToken = responseData.data;
        setVerifyToken(verifyToken);
        setCurrentShift(shiftToApprove);
        setOtpType("singleApprove");
        setShowOTPModal(true);
      }
    } catch (error) {
      console.error("Error in handleApprove:", error);
      if (
        error.response.data.message === "Employer Mobile number not verified"
      ) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Access Denied: You don't have permission");
      }
      // toast.error("Access Denied: You don't have permission");

      // setErrorMessage("Failed to initiate approval. Please try again.");
      // setShowErrorModal(true);
    }
  };

  const handleDispute = (shift) => {
    setCurrentShift(shift);
    setShowDisputeModal(true);
  };

  const handleDisputeSubmit = async (category, comment) => {
    if (!currentShift) return;

    try {
      // Prepare shift data with UPPERCASE field names
      const shiftData = {
        PROFESSIONAL_NAME: currentShift.professionalName || "",
        JOB_ID: currentShift.id || "",
        BRANCH_NAME: currentShift.hospitalBranch || "",
        SHIFT_START_DATE_TIME: currentShift.shiftStartDateTime || "",
        SHIFT_END_DATE_TIME: currentShift.shiftEndDateTime || "",
        CURRENT_STATUS: "PENDING_APPROVAL" || "",
      };

      let criteria = {
        REQUESTED_STATUS: "DISPUTED",
        DISPUTE_REASON: category,
        DISPUTE_COMMENT: comment,
        shifts: [shiftData],
      };

      // Call OTP API with the shift in an array
      const response = await axiosInstance.post(`/common/sendOtp`, criteria);
      // Call OTP API with the shift and dispute details
      // const response = await fetch('https://jobizo-admin.jobizo.com/common/sendOtp', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'Authorization': `Bearer ${get_Token()}`
      //   },
      //   body: JSON.stringify({
      //     REQUESTED_STATUS: 'DISPUTED',
      //     DISPUTE_REASON: category,
      //     DISPUTE_COMMENT: comment,
      //     shifts: [shiftData],
      //   }),
      // });

      // if (!response.ok) {
      //   throw new Error('Failed to send OTP for dispute');
      // }

      const responseData = await response;
      const verifyToken = responseData.data;
      setVerifyToken(verifyToken);

      // Store dispute data for later submission after OTP verification
      setPendingDisputeData({ category, comment });
      setOtpType("dispute");
      setShowDisputeModal(false);
      setShowOTPModal(true);
    } catch (error) {
      console.error("Error initiating dispute:", error);
      // toast.error("Access Denied: You don't have permission");
      if (
        error.response.data.message === "Employer Mobile number not verified"
      ) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Access Denied: You don't have permission");
      }

      // setErrorMessage("Failed to initiate dispute. Please try again.");
      // setShowErrorModal(true);
    }
  };

  const handleResolve = async (shiftId) => {
    try {
      const shiftToResolve = shifts.find((shift) => shift.id === shiftId);
      if (!shiftToResolve) return;

      // Prepare shift data with UPPERCASE field names
      const shiftData = {
        PROFESSIONAL_NAME: shiftToResolve.professionalName || "",
        JOB_ID: shiftToResolve.id || "",
        BRANCH_NAME: shiftToResolve.hospitalBranch || "",
        SHIFT_START_DATE_TIME: shiftToResolve.shiftStartDateTime || "",
        SHIFT_END_DATE_TIME: shiftToResolve.shiftEndDateTime || "",
        CURRENT_STATUS: "DISPUTED" || "",
      };
      let criteria = {
        REQUESTED_STATUS: "RESOLVED",
        shifts: [shiftData],
      };

      // Call OTP API with the shift in an array
      const response = await axiosInstance.post(`/common/sendOtp`, criteria);
      // Call OTP API with the shift data
      // const response = await fetch('https://jobizo-admin.jobizo.com/common/sendOtp', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'Authorization': `Bearer ${get_Token()}`
      //   },
      //   body: JSON.stringify({
      //     REQUESTED_STATUS: 'RESOLVED',
      //     shifts: [shiftData],
      //   }),
      // });

      // if (!response.ok) {
      //   throw new Error('Failed to send OTP for resolution');
      // }

      const responseData = await response;
      const verifyToken = responseData.data;
      setVerifyToken(verifyToken);

      setCurrentShift(shiftToResolve);
      setOtpType("resolve");
      setShowOTPModal(true);
    } catch (error) {
      console.error("Error in handleResolve:", error);
      if (
        error.response.data.message === "Employer Mobile number not verified"
      ) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Access Denied: You don't have permission");
      }
      // toast.error("Access Denied: You don't have permission");

      // setErrorMessage("Failed to initiate resolution. Please try again.");
      // setShowErrorModal(true);
    }
  };

  // const handleOTPApprove = (idsToApprove) => {
  //   const isBulk = Array.isArray(idsToApprove);
  //   const shiftsToUpdate = isBulk ? idsToApprove : [idsToApprove];

  //   setShifts((prevShifts) =>
  //     prevShifts.map((shift) =>
  //       shiftsToUpdate.includes(shift.id)
  //         ? {
  //             ...shift,
  //             status: "Approved",
  //             logs: [
  //               ...shift.logs,
  //               {
  //                 action: "Approved",
  //                 timestamp: new Date().toISOString(),
  //                 status: "Success",
  //               },
  //             ],
  //           }
  //         : shift
  //     )
  //   );

  //   setSelectedShifts((prev) =>
  //     prev.filter((id) => !shiftsToUpdate.includes(id))
  //   );
  //   setShowOTPModal(false);
  //   setCurrentShift(null); // Clear current shift after single approval
  //   setPendingBulkApproveIds([]); // Clear bulk IDs after bulk approval

  //   // Show success modal
  //   if (isBulk) {
  //     const count = shiftsToUpdate.length;
  //     const shiftIds = shiftsToUpdate.join(", ");
  //     setSuccessMessage(
  //       `${count} shift${count > 1 ? "s" : ""} (${shiftIds}) ${
  //         count > 1 ? "have" : "has"
  //       } been successfully approved.`
  //     );
  //     setSuccessType("bulk");
  //   } else {
  //     const approvedShift = shifts.find((s) => s.id === idsToApprove);
  //     setSuccessMessage(
  //       `Shift ${approvedShift?.id} for ${approvedShift?.professionalName} has been successfully approved.`
  //     );
  //     setSuccessType("single");
  //   }
  //   setShowSuccessModal(true);
  // };
  const handleOTPApprove = (updates) => {
    const isBulk = Array.isArray(updates);

    const jobIds = isBulk ? updates.map((item) => item.jobId) : [updates];

    setShifts((prevShifts) =>
      prevShifts.map((shift) =>
        jobIds.includes(shift.jobId)
          ? {
              ...shift,
              status: "Approved",
              logs: [
                ...(shift.logs || []),
                {
                  action: "Approved",
                  timestamp: new Date().toISOString(),
                  status: "Success",
                },
              ],
            }
          : shift,
      ),
    );

    setSelectedShifts([]);

    setShowOTPModal(false);
    setCurrentShift(null);
    setPendingBulkApproveIds([]);

    if (isBulk) {
      setSuccessMessage(
        `${updates.length} shifts have been successfully approved.`,
      );
      setSuccessType("bulk");
    } else {
      const approvedShift = shifts.find((shift) => shift.id === updates);

      setSuccessMessage(
        `Shift ${approvedShift?.id} for ${approvedShift?.professionalName} has been successfully approved.`,
      );
      setSuccessType("single");
    }

    setShowSuccessModal(true);
  };
  // const handleBulkApprove = async () => {
  //   if (selectedShifts.length === 0) {
  //     alert("Please select at least one shift to approve.");
  //     return;
  //   }

  //   try {
  //     // Get all selected shift objects with UPPERCASE field names
  //     const shiftsToApprove = shifts
  //       .filter(
  //         (shift) =>
  //           selectedShifts.includes(`${shift.id}-${shift.professionalName}`) &&
  //           shift.status === "Pending"
  //       )
  //       .map((shift) => ({
  //         PROFESSIONAL_NAME: shift.professionalName || "",
  //         JOB_ID: shift.id || "",
  //         BRANCH_NAME: shift.hospitalBranch || "",
  //         SHIFT_START_DATE_TIME: shift.shiftStartDateTime || "",
  //         SHIFT_END_DATE_TIME: shift.shiftEndDateTime || "",
  //         CURRENT_STATUS: "PENDING_APPROVAL" || "",
  //       }));

  //     if (shiftsToApprove.length === 0) {
  //       alert("No pending shifts selected for approval.");
  //       return;
  //     }
  //     let criteria = {
  //       REQUESTED_STATUS: "APPROVED",
  //       shifts: shiftsToApprove,
  //     };

  //     // Call OTP API with the shift in an array
  //     const response = await axiosInstance.post(`/common/sendOtp`, criteria);

  //     // Call OTP API with all selected shifts
  //     // const response = await fetch('https://jobizo-admin.jobizo.com/common/sendOtp', {
  //     //   method: 'POST',
  //     //   headers: {
  //     //     'Content-Type': 'application/json',
  //     //     'Authorization': `Bearer ${get_Token()}`
  //     //   },
  //     //   body: JSON.stringify({
  //     //     REQUESTED_STATUS: 'APPROVED',
  //     //     shifts: shiftsToApprove,
  //     //   }),
  //     // });

  //     // if (!response.ok) {
  //     //   throw new Error('Failed to send OTP');
  //     // }

  //     const responseData = await response;
  //     const verifyToken = responseData.data;
  //     setVerifyToken(verifyToken);

  //     // Create an array of objects with jobId and userId for each selected shift
  //     const shiftsData = selectedShifts.map((id) => {
  //       const shift = shifts.find(
  //         (s) => `${s.id}-${s.professionalName}` === id
  //       );
  //       return {
  //         jobId: shift?.jobId,
  //         userId: shift?.userId,
  //       };
  //     });

  //     setPendingBulkApproveIds(shiftsData);
  //     setOtpType("bulkApprove");
  //     setShowOTPModal(true);
  //   } catch (error) {
  //     console.error("Error in handleBulkApprove:", error);
  //     // setErrorMessage("Failed to initiate bulk approval. Please try again.");
  //     // setShowErrorModal(true);
  //   toast.error("Access Denied: You don't have permission");

  //   }
  // };

  const handleBulkApprove = async () => {
    if (selectedShifts.length === 0) {
      toast.error("Please select at least one shift to approve.");
      return;
    }

    try {
      const selectedPendingShifts = shifts.filter(
        (shift) =>
          selectedShifts.includes(`${shift.id}-${shift.professionalName}`) &&
          shift.status === "Pending",
      );

      if (selectedPendingShifts.length === 0) {
        toast.error("No pending shifts selected for approval.");
        return;
      }

      const shiftsToApprove = selectedPendingShifts.map((shift) => ({
        PROFESSIONAL_NAME: shift.professionalName || "",
        JOB_ID: shift.id || "",
        BRANCH_NAME: shift.hospitalBranch || "",
        SHIFT_START_DATE_TIME: shift.shiftStartDateTime || "",
        SHIFT_END_DATE_TIME: shift.shiftEndDateTime || "",
        CURRENT_STATUS: "PENDING_APPROVAL",
      }));

      const criteria = {
        REQUESTED_STATUS: "APPROVED",
        shifts: shiftsToApprove,
      };

      const response = await axiosInstance.post("/common/sendOtp", criteria);

      const verifyToken = response.data;

      setVerifyToken(verifyToken);

      const updates = selectedPendingShifts.map((shift) => ({
        jobId: shift.jobId,
        userId: shift.userId,
        approved: "APPROVED",
        disputedReason: "",
        disputedComment: "",
      }));

      setPendingBulkApproveIds(updates);

      setOtpType("bulkApprove");
      setShowOTPModal(true);
    } catch (error) {
      console.error("Error in handleBulkApprove:", error);

      toast.error(
        error?.response?.data?.message || "Failed to initiate bulk approval",
      );
    }
  };
  const handleShowLogs = (shift) => {
    setCurrentShift(shift);
    setShowLogsModal(true);
  };

  const handleSelect = (shiftId, professionalName) => {
    const compositeKey = `${shiftId}-${professionalName}`;
    setSelectedShifts((prev) =>
      prev.includes(compositeKey)
        ? prev.filter((id) => id !== compositeKey)
        : [...prev, compositeKey],
    );
  };

  // const handleSelectAll = (isChecked) => {
  //   if (isChecked) {
  //     const pendingShifts = filteredShifts
  //       .filter((shift) => shift.status === "Pending")
  //       .map((shift) => `${shift.id}-${shift.professionalName}`);
  //     setSelectedShifts(pendingShifts);
  //   } else {
  //     setSelectedShifts([]);
  //   }
  // };

  const handleSelectAll = (isChecked) => {
    const pendingKeys = filteredShifts
      .filter((shift) => shift.status === "Pending")
      .map((shift) => `${shift.id}-${shift.professionalName}`);

    if (isChecked) {
      setSelectedShifts(pendingKeys);
    } else {
      setSelectedShifts([]);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      Pending: `${styles.statusBadge} ${styles.statusPending}`,
      Approved: `${styles.statusBadge} ${styles.statusApproved}`,
      Disputed: `${styles.statusBadge} ${styles.statusDisputed}`,
      Resolved: `${styles.statusBadge} ${styles.statusResolved}`,
    };
    const displayText = status === "Pending" ? "PENDING APPROVAL" : status;
    return <span className={statusClasses[status]}>{displayText}</span>;
  };

  const getAutoCheckOutBadge = (isAutoCheckOut) => (
    <span
      className={`${styles.statusBadge} ${
        isAutoCheckOut ? styles.autoCheckOutYes : styles.autoCheckOutNo
      }`}
    >
      {isAutoCheckOut ? "Yes" : "No"}
    </span>
  );

  const getAutoCheckInBadge = (isAutoCheckIn) => (
    <span
      className={`${styles.statusBadge} ${
        isAutoCheckIn ? styles.autoCheckOutYes : styles.autoCheckOutNo
      }`}
    >
      {isAutoCheckIn ? "Yes" : "No"}
    </span>
  );

  const getOflStatusBadge = (status) => {
    if (!status) return "—";
    const statusClasses = {
      Early: styles.oflStatusEarly,
      Late: styles.oflStatusLate,
      "Completed On Time": styles.oflStatusOnTime,
      "On time": styles.oflStatusOnTime,
      "Early/Late shift completion": styles.oflStatusEarlyLate,
      "Out of location": styles.oflStatusOutOfLocation,
    };
    return (
      <span className={`${styles.statusBadge} ${statusClasses[status] || ""}`}>
        {status}
      </span>
    );
  };

  const getProximityScoreBadge = (score) => {
    const scoreClasses = {
      High: styles.gpsScoreHigh,
      Medium: styles.gpsScoreMedium,
      Low: styles.gpsScoreLow,
      "Not Identified": styles.gpsScoreUnknown,
    };
    return (
      <span
        className={`${styles.statusBadge} ${scoreClasses[score] || styles.gpsScoreUnknown}`}
      >
        {score}
      </span>
    );
  };

  const formatOflCheckOutTime = (timestamp) => {
    if (!timestamp) return "—";
    return (
      <div className={styles.oflCheckOutTimeCell}>
        <span className={styles.oflCheckOutTime}>
          {dayjs(Number(timestamp)).format("MMM D, YYYY, h:mm A")}
        </span>
        <span className={styles.userEnteredLabel}>user-entered</span>
      </div>
    );
  };

  const renderActionCell = (shift) => {
    // console.log("SHIFT DATA:", shift);
    return (
      <div className={styles.actionButtons}>
        {shift.status === "Pending" ? (
          <>
            <button
              className={styles.approveBtn}
              disabled={allPendingSelected}
              title={
                allPendingSelected
                  ? "Use 'Approve Selected' to approve all selected shifts"
                  : "Approve shift"
              }
              onClick={() => {
                if (!allPendingSelected) {
                  handleApprove(shift.id);
                }
              }}
              // onClick={() => handleApprove(shift.id)}
            >
              Approve
            </button>
            <button
              className={styles.disputeBtn}
              onClick={() => handleDispute(shift)}
            >
              Dispute
            </button>
          </>
        ) : shift.status === "Disputed" ? (
          <button
            className={styles.approveBtn}
            onClick={() => handleResolve(shift.id)}
          >
            Resolve
          </button>
        ) : (
          <button
            className={styles.viewLogsBtn}
            onClick={() => handleShowLogs(shift)}
            title="View Logs"
          >
            <svg
              className={styles.eyeIcon}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
                fill="currentColor"
              />
            </svg>
          </button>
        )}
        <button
          className={styles.pingsBtn}
          // onClick={() => setPingsShift(shift)}
          onClick={() => {
            setPingsShift({
              ...shift,
              locationPings: Array.isArray(shift.locationPings)
                ? shift.locationPings
                : normalizeLocationPings(shift),
            });
          }}
          title="View hourly location pings"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="9" />
            <line x1="12" y1="3" x2="12" y2="6" />
            <line x1="12" y1="18" x2="12" y2="21" />
            <line x1="3" y1="12" x2="6" y2="12" />
            <line x1="18" y1="12" x2="21" y2="12" />
            <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
          </svg>
        </button>
      </div>
    );
  };

  // Order groups all check-in fields together, then all check-out fields.
  const columnDefs = [
    {
      key: "id",
      label: "Job ID",
      locked: true,
      cellClassName: styles.jobId,
      render: (shift) => (
        <Link
          to={`/jobDetails/code/${shift.id}/${shift.jobId}`}
          className={styles.jobLink}
        >
          {shift.id}
        </Link>
      ),
    },
    {
      key: "hospitalBranch",
      label: "Hospital Branch",
      cellClassName: styles.hospitalBranch,
      render: (shift) => shift.hospitalBranch,
    },
    {
      key: "specialization",
      label: "Specialization",
      cellClassName: styles.specialization,
      render: (shift) => shift.specialization,
    },
    {
      key: "professionalName",
      label: "Professional Name",
      cellClassName: styles.professionalName,
      render: (shift) => shift.professionalName,
    },
    {
      key: "phoneNumber",
      label: "Phone Number",
      cellClassName: styles.phoneNumber,
      render: (shift) => shift.phoneNumber,
    },
    {
      key: "panNumber",
      label: "Pan Number",
      cellClassName: styles.panNumber,
      render: (shift) => shift.panNumber,
    },

    {
      key: "hospitalCity",
      label: "Hospital City",
      cellClassName: styles.hospitalCity,
      render: (shift) => shift.hospitalCity,
    },

    {
      key: "profession",
      label: "Profession",
      cellClassName: styles.profession,
      render: (shift) => shift.profession,
    },
    {
      key: "department",
      label: "Department",
      cellClassName: styles.department,
      render: (shift) => shift.department,
    },
    {
      key: "campDuty",
      label: "Camp Duty",
      cellClassName: styles.campDuty,
      render: (shift) => shift.campDuty,
    },
    {
      key: "shiftStartDateTime",
      label: "Shift Start Date & Time",
      cellClassName: styles.shiftDateTime,
      render: (shift) => shift.shiftStartDateTime,
    },
    {
      key: "shiftEndDateTime",
      label: "Shift End Date & Time",
      cellClassName: styles.shiftDateTime,
      render: (shift) => shift.shiftEndDateTime,
    },
    {
      key: "in",
      label: "Check In Date & Time",
      headerTitle: "First ping of the confirming pair — see Auto Check-In BRD",
      cellClassName: styles.shiftDateTime,
      render: (shift) => shift.in,
    },
    {
      key: "autoCheckIn",
      label: "Auto Check-In",
      info: "autoCheckIn",
      cellClassName: styles.statusCell,
      render: (shift) => getAutoCheckInBadge(shift.autoCheckIn),
    },
    {
      key: "checkInProximityScore",
      label: "Check-In Proximity Score",
      headerTitle: CHECK_IN_PROXIMITY_HEADER_HINT,
      info: "checkIn",
      cellClassName: styles.statusCell,
      render: (shift) => getProximityScoreBadge(shift.checkInProximityScore),
    },
    {
      key: "oflCheckInTime",
      label: "OFL Check-In Time",
      cellClassName: styles.shiftDateTime,
      render: (shift) => formatOflCheckOutTime(shift.oflCheckInTime),
    },
    {
      key: "oflCheckInStatus",
      label: "OFL Check-In Status",
      cellClassName: styles.statusCell,
      render: (shift) => getOflStatusBadge(shift.oflCheckInStatus),
    },
    {
      key: "out",
      label: "Check Out Date & Time",
      cellClassName: styles.shiftDateTime,
      render: (shift) => shift.out,
    },
    {
      key: "autoCheckOut",
      label: "Auto Check-Out",
      cellClassName: styles.statusCell,
      render: (shift) => getAutoCheckOutBadge(shift.autoCheckOut),
    },
    {
      key: "oflCheckOutTime",
      label: "OFL Check-out Time",
      cellClassName: styles.shiftDateTime,
      render: (shift) => formatOflCheckOutTime(shift.oflCheckOutTime),
    },
    {
      key: "checkOutProximityScore",
      label: "Check-Out Proximity Score",
      info: "checkOut",
      cellClassName: styles.statusCell,
      render: (shift) => getProximityScoreBadge(shift.checkOutProximityScore),
    },
    {
      key: "totalCost",
      label: "Total Cost",
      cellClassName: styles.totalCost,
      render: (shift) => shift.totalCost,
    },
    {
      key: "status",
      label: "Status",
      cellClassName: styles.statusCell,
      render: (shift) => getStatusBadge(shift.status),
    },
    {
      key: "actualJobHours",
      label: "Actual Job Hours",
      cellClassName: styles.actualJobHours,
      cellStyle: { textAlign: "center" },
      render: (shift) => shift.actualJobHours,
    },
    {
      key: "approveAt",
      label: "Approve At",
      cellClassName: styles.approveAt,
      cellStyle: { textAlign: "center" },
      render: (shift) =>
        shift.approveAt
          ? moment(Number(shift.approveAt)).format("YYYY/MM/DD")
          : "-",
    },
    {
      key: "approveBy",
      label: "Approve By",
      cellClassName: styles.approveBy,
      cellStyle: { textAlign: "center" },
      render: (shift) => (shift.approveBy ? shift.approveBy : "-"),
    },
    {
      key: "action",
      label: "Action",
      locked: true,
      headerClassName: styles.actionHeader,
      cellClassName: styles.actionCell,
      render: renderActionCell,
    },
  ];

  const visibleColumns = columnDefs.filter(
    (column) => column.locked || !hiddenColumns[column.key],
  );

  const persistHiddenColumns = (next) => {
    setHiddenColumns(next);
    try {
      window.localStorage.setItem(
        COLUMN_VISIBILITY_STORAGE_KEY,
        JSON.stringify(next),
      );
    } catch (e) {
      // Storage unavailable (private mode) — visibility stays for this session.
    }
  };

  const handleToggleColumn = (key, isVisible) => {
    persistHiddenColumns({ ...hiddenColumns, [key]: !isVisible });
  };

  const handleSetAllColumns = (showAll) => {
    const next = {};
    columnDefs.forEach((column) => {
      if (column.locked) return;
      next[column.key] = !showAll;
    });
    persistHiddenColumns(next);
  };

  const columnMenuItems = columnDefs.filter((column) =>
    column.label.toLowerCase().includes(columnSearch.trim().toLowerCase()),
  );

  const hiddenColumnCount = columnDefs.filter(
    (column) => !column.locked && hiddenColumns[column.key],
  ).length;

  // const showCheckboxes = filters.status === "Pending";
  // const showSelectAll = showCheckboxes && filteredShifts.length > 0;
  const pendingShifts = filteredShifts.filter(
    (shift) => shift.status === "Pending",
  );

  const allPendingSelected =
    pendingShifts.length > 0 &&
    pendingShifts.every((shift) =>
      selectedShifts.includes(`${shift.id}-${shift.professionalName}`),
    );

  const showCheckboxes = pendingShifts.length > 0;
  const showSelectAll = pendingShifts.length > 0;

  // Function to refresh the shift list
  const refreshShiftList = () => {
    // console.log("inside refreshShiftList");
    const bothDatesSelected =
      filters.startDate && filters.endDate ? true : false;

    if (!bothDatesSelected && (filters.startDate || filters.endDate)) return;

    const branchForPayload =
      filters.branch && filters.branch !== "Branch" ? filters.branch : "";

    const professionForPayload =
      filters.profession && filters.profession !== "Profession"
        ? filters.profession
        : "";

    // ✅ Convert to YYYY-MM-DD format
    const formattedStartDate = filters.startDate
      ? new Date(filters.startDate).toISOString().split("T")[0]
      : "";
    const formattedEndDate = filters.endDate
      ? new Date(filters.endDate).toISOString().split("T")[0]
      : "";

    const autoCheckOutForPayload = filters.autoCheckOut === "Yes" ? true : "";

    dispatch(
      listShiftLogs({
        approvedStatus: getApiStatus(filters.status),
        filterByHospitalBranch: branchForPayload,
        filterByProfession: professionForPayload,
        filterByAutoCheckOut: autoCheckOutForPayload,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        currentPage: currentPage1,
        search: filters.searchKey,
        pageSize: pageSize1,
      }),
    );
  };

  // console.log("currentItems", currentItems);

  // console.log("pingsShift", pingsShift);

  return (
    <>
      {" "}
      <DashboardLayout>
        {" "}
        <DashboardNavbar />{" "}
        <div className={styles.container}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.titleSection}>
              <div className={styles.indicator}></div>
              <h1>Shift Logs</h1>
            </div>
            <div className={styles.breadcrumb}>
              <span>Locum</span>
              <span className={styles.separator}>{">"}</span>
              <span>Shift Logs</span>
            </div>
          </div>

          {/* Filters */}

          <FiltersSection
            filters={filters}
            onFiltersChange={setFilters}
            showSelectAll={showSelectAll}
            selectedCount={selectedShifts.length}
            totalPending={
              filteredShifts.filter((shift) => shift.status === "Pending")
                .length
            }
            onSelectAll={handleSelectAll}
            onBulkApprove={handleBulkApprove}
            branchOptions={branchOptions} // Add this line
            professionOptions={professionOptions}
            handleDownloadData={handleDownloadData}
            loadingExport={loadingExport}
            handleResetFilters={handleResetFilters}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
          />

          {/* Bulk Approval Action */}
          {selectedShifts.length > 0 && (
            <div className={styles.bulkActionBar}>
              <div className={styles.bulkSelectionInfo}>
                <span className={styles.bulkCheckIcon}>✓</span>

                <div>
                  <strong>
                    {selectedShifts.length} shift
                    {selectedShifts.length > 1 ? "s" : ""} selected
                  </strong>

                  <span className={styles.bulkSelectionHint}>
                    {allPendingSelected
                      ? " All pending shifts are selected"
                      : " Selected shifts are ready for approval"}
                  </span>
                </div>
              </div>

              <button
                type="button"
                className={styles.bulkApproveButton}
                // onClick={handleBulkApprove}
                onClick={() => setShowBulkApproveModal(true)}
              >
                Approve Selected ({selectedShifts.length})
              </button>
            </div>
          )}

          {/* Column visibility */}
          <div className={styles.tableToolbar}>
            <div className={styles.colMenuWrap}>
              <button
                type="button"
                className={styles.toolbarBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowColumnMenu((open) => !open);
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="7" height="18" />
                  <rect x="14" y="3" width="7" height="18" />
                </svg>
                Columns
                {hiddenColumnCount > 0 && (
                  <span className={styles.colCountBadge}>
                    {hiddenColumnCount} hidden
                  </span>
                )}
              </button>

              {showColumnMenu && (
                <div
                  className={styles.colMenu}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="text"
                    className={styles.colSearch}
                    placeholder="Find column"
                    value={columnSearch}
                    onChange={(e) => setColumnSearch(e.target.value)}
                  />
                  <div className={styles.colList}>
                    {columnMenuItems.length === 0 ? (
                      <div className={styles.colEmpty}>No columns found</div>
                    ) : (
                      columnMenuItems.map((column) => {
                        const isVisible =
                          column.locked || !hiddenColumns[column.key];
                        return (
                          <div
                            key={column.key}
                            className={`${styles.colRow} ${
                              column.locked ? styles.colRowLocked : ""
                            }`}
                          >
                            <span className={styles.colLabel}>
                              {column.label}
                            </span>
                            <label className={styles.switch}>
                              <input
                                type="checkbox"
                                checked={isVisible}
                                disabled={column.locked}
                                onChange={(e) =>
                                  handleToggleColumn(
                                    column.key,
                                    e.target.checked,
                                  )
                                }
                              />
                              <span className={styles.switchTrack} />
                            </label>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className={styles.colMenuFooter}>
                    <button
                      type="button"
                      onClick={() => handleSetAllColumns(false)}
                    >
                      HIDE ALL
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSetAllColumns(true)}
                    >
                      SHOW ALL
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Table */}
          <div
            className={styles.tableContainer}
            style={{
              minHeight: "calc(100vh - 300px)", // Adjust this value based on your header/footer
              position: "relative",

              paddingBottom: "60px", // Make space for pagination
            }}
          >
            {loading && (
              <div className={styles.loaderOverlay}>
                <div className={styles.spinner}></div>
              </div>
            )}
            {!shiftLogs || shiftLogs.length === 0 ? (
              <div className={styles.emptyState}>
                <p style={{ marginTop: "15px" }}>
                  No shift logs found matching your criteria
                </p>
              </div>
            ) : (
              <table
                className={styles.table}
                style={{
                  borderCollapse: "collapse",
                  width: "100%",
                  border: "none",
                }}
              >
                <thead>
                  <tr style={{ border: "none" }}>
                    {/* {showCheckboxes && (
                      <th
                        className={styles.checkboxHeader}
                        style={{ border: "none" }}
                      >
                        {showSelectAll && (
                          <input
                            type="checkbox"
                            checked={
                              selectedShifts.length ===
                                filteredShifts.filter(
                                  (shift) => shift.status === "Pending",
                                ).length && filteredShifts.length > 0
                            }
                            onChange={(e) => handleSelectAll(e.target.checked)}
                          />
                        )}
                      </th>
                    )} */}
                    {showCheckboxes && (
                      <th
                        className={styles.checkboxHeader}
                        style={{ border: "none" }}
                      >
                        <input
                          type="checkbox"
                          // checked={
                          //   pendingShifts.length > 0 &&
                          //   pendingShifts.every((shift) =>
                          //     selectedShifts.includes(
                          //       `${shift.id}-${shift.professionalName}`,
                          //     ),
                          //   )
                          // }
                          checked={allPendingSelected}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </th>
                    )}
                    {visibleColumns.map((column) => (
                      <th
                        key={column.key}
                        className={column.headerClassName}
                        style={{ border: "none" }}
                        title={column.headerTitle}
                      >
                        {column.label}
                        {column.info && (
                          <button
                            type="button"
                            className={styles.infoBtn}
                            title="How this works"
                            onClick={(e) => {
                              e.stopPropagation();
                              setInfoKey(column.info);
                            }}
                          >
                            &#9432;
                          </button>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((shift, index) => (
                    <tr
                      key={`${shift.id}-${shift.userId || index}`}
                      style={{ border: "none" }}
                    >
                      {/* {showCheckboxes && (
                        <td
                          className={styles.checkboxCell}
                          style={{ border: "none" }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedShifts.includes(
                              `${shift.id}-${shift.professionalName}`,
                            )}
                            onChange={() =>
                              handleSelect(shift.id, shift.professionalName)
                            }
                          />
                        </td>
                      )} */}
                      {showCheckboxes && (
                        <td
                          className={styles.checkboxCell}
                          style={{ border: "none" }}
                        >
                          {shift.status === "Pending" && (
                            <input
                              type="checkbox"
                              checked={selectedShifts.includes(
                                `${shift.id}-${shift.professionalName}`,
                              )}
                              onChange={() =>
                                handleSelect(shift.id, shift.professionalName)
                              }
                            />
                          )}
                        </td>
                      )}
                      {visibleColumns.map((column) => (
                        <td
                          key={column.key}
                          className={column.cellClassName}
                          style={{
                            border: "none",
                            ...(column.cellStyle || {}),
                          }}
                        >
                          {column.render(shift)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Modals */}
          {showDisputeModal && (
            <DisputeModal
              shift={currentShift}
              onClose={() => setShowDisputeModal(false)}
              onSubmit={handleDisputeSubmit}
            />
          )}

          {showLogsModal && (
            <ActionLogsModal
              shift={currentShift}
              onClose={() => setShowLogsModal(false)}
            />
          )}

          <SuccessModal
            isOpen={showSuccessModal}
            onClose={() => setShowSuccessModal(false)}
            message={successMessage}
            type={successType}
            otpType={otpType}
          />

          <OTPModal
            isOpen={showOTPModal}
            onClose={() => {
              setShowOTPModal(false);
              setCurrentShift(null);
              setPendingDisputeData(null);
              setPendingBulkApproveIds([]); // Clear pending bulk IDs on close
            }}
            onSubmit={
              otpType === "singleApprove"
                ? handleApprove
                : otpType === "bulkApprove"
                  ? handleBulkApprove
                  : otpType === "dispute"
                    ? handleDisputeSubmit
                    : handleResolve
            }
            type={otpType}
            shift={currentShift}
            disputeData={pendingDisputeData}
            bulkApproveIds={pendingBulkApproveIds}
            verifyToken={verifyToken}
            get_Token={get_Token}
            refreshShiftList={refreshShiftList}
            onSuccess={() => {
              setSuccessMessage(
                otpType === "bulkApprove"
                  ? "Shifts approved successfully!"
                  : otpType === "dispute"
                    ? "Dispute raised successfully!"
                    : otpType === "singleApprove"
                      ? "Shift approved successfully!"
                      : otpType === "resolve"
                        ? "Shift resolved successfully!"
                        : "Action completed successfully!",
              );
              setShowSuccessModal(true);
            }}
            handleResetFilters={handleResetFilters}
          />
          {showErrorModal && (
            <div className={styles.errorModal}>
              <p className={styles.errorText}>{errorMessage}</p>
              <button
                className={styles.retryButton}
                onClick={() => setShowErrorModal(false)}
              >
                Close
              </button>
            </div>
          )}
          {/* Hourly in-shift location pings — display only, not part of export */}
          {pingsShift && (
            <div
              className={styles.drawerOverlay}
              onClick={() => setPingsShift(null)}
            >
              <div
                className={styles.drawer}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className={styles.drawerClose}
                  onClick={() => setPingsShift(null)}
                >
                  &times;
                </button>
                <h3>{pingsShift.id}</h3>
                <div className={styles.drawerSub}>
                  Hourly in-shift location pings
                </div>
                <div className={styles.pingsNote}>
                  <ul>
                    <li>
                      Starts once the shift is checked in — auto or manual.
                    </li>
                    <li>Sends one GPS ping every 60 min after that.</li>
                    <li>
                      Keeps going until check-out is done — auto or manual.
                    </li>
                    <li>
                      For ops visibility only — does not affect check-in or
                      check-out time.
                    </li>
                  </ul>
                </div>
                <div className={styles.scoreLegend}>
                  Low (&gt;200m) &nbsp;·&nbsp; Medium (50–200m) &nbsp;·&nbsp;
                  High (&lt;50m) &nbsp;·&nbsp; Not Identified (no GPS data)
                </div>
                {(pingsShift.locationPings || []).length === 0 ? (
                  <p className={styles.pingsEmpty}>
                    No location pings recorded for this shift yet.
                  </p>
                ) : (
                  (pingsShift.locationPings || []).map((ping, index) => (
                    <div
                      key={`${pingsShift.id}-ping-${index}`}
                      className={styles.pingRow}
                    >
                      <span className={styles.pingNum}>{index + 1}</span>
                      <span className={styles.pingMain}>
                        <span className={styles.pingTime}>
                          {ping.timestamp
                            ? dayjs(ping.timestamp).format(
                                "MMM D, YYYY, h:mm A",
                              )
                            : "—"}
                          {ping.source ? ` (${ping.source})` : ""}
                        </span>
                        <span className={styles.pingDist}>
                          {ping.distance === null || Number.isNaN(ping.distance)
                            ? "No GPS signal"
                            : `${ping.distance}m from hospital`}
                        </span>
                      </span>
                      {getProximityScoreBadge(ping.score)}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Column header explainers */}
          {infoKey && INFO_CONTENT[infoKey] && (
            <div
              className={styles.drawerOverlay}
              onClick={() => setInfoKey(null)}
            >
              <div
                className={`${styles.drawer} ${styles.infoDrawer}`}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className={styles.drawerClose}
                  onClick={() => setInfoKey(null)}
                >
                  &times;
                </button>
                <h3>{INFO_CONTENT[infoKey].title}</h3>
                {INFO_CONTENT[infoKey].sub && (
                  <div className={styles.drawerSub}>
                    {INFO_CONTENT[infoKey].sub}
                  </div>
                )}
                {INFO_CONTENT[infoKey].body}
              </div>
            </div>
          )}

          {showBulkApproveModal && (
            <div
              className={styles.bulkModalOverlay}
              onClick={() => setShowBulkApproveModal(false)}
            >
              <div
                className={styles.bulkApproveModal}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close */}
                <button
                  type="button"
                  className={styles.bulkModalClose}
                  onClick={() => setShowBulkApproveModal(false)}
                >
                  ×
                </button>

                {/* Icon */}
                <div className={styles.bulkModalIcon}>✓</div>

                {/* Heading */}
                <h2 className={styles.bulkModalTitle}>
                  Approve Selected Shifts
                </h2>

                <p className={styles.bulkModalDescription}>
                  You are about to approve all selected pending shifts.
                  <br />
                  Please review the summary below before proceeding.
                </p>

                {/* Summary */}
                <div className={styles.bulkSummary}>
                  <div className={styles.bulkSummaryRow}>
                    <div className={styles.bulkSummaryLeft}>
                      <span className={styles.summaryIcon}>✓</span>
                      <span>Total shifts selected</span>
                    </div>

                    <strong>{selectedShifts.length}</strong>
                  </div>

                  <div className={styles.bulkSummaryRow}>
                    <div className={styles.bulkSummaryLeft}>
                      <span className={styles.summaryIcon}>♟</span>
                      <span>Professionals involved</span>
                    </div>

                    <strong>
                      {
                        new Set(
                          shifts
                            .filter((shift) =>
                              selectedShifts.includes(
                                `${shift.id}-${shift.professionalName}`,
                              ),
                            )
                            .map((shift) => shift.userId),
                        ).size
                      }
                    </strong>
                  </div>

                  <div className={styles.bulkSummaryRow}>
                    <div className={styles.bulkSummaryLeft}>
                      <span className={styles.summaryIcon}>▣</span>
                      <span>Date range</span>
                    </div>

                    <strong>
                      {(() => {
                        const selected = shifts.filter((shift) =>
                          selectedShifts.includes(
                            `${shift.id}-${shift.professionalName}`,
                          ),
                        );

                        if (!selected.length) return "-";

                        const dates = selected
                          .map((shift) => dayjs(shift.shiftStartDateTime))
                          .filter((date) => date.isValid())
                          .sort((a, b) => a.valueOf() - b.valueOf());

                        if (!dates.length) return "-";

                        const start = dates[0];
                        const end = dates[dates.length - 1];

                        return `${start.format("MMM D")} – ${end.format(
                          "MMM D, YYYY",
                        )}`;
                      })()}
                    </strong>
                  </div>
                </div>

                {/* Warning */}
                <div className={styles.bulkWarning}>
                  <span>⚠</span>

                  <span>
                    Once approved, these shifts cannot be reverted without
                    dispute.
                  </span>
                </div>

                {/* Buttons */}
                <div className={styles.bulkModalActions}>
                  <button
                    type="button"
                    className={styles.bulkCancelButton}
                    onClick={() => setShowBulkApproveModal(false)}
                  >
                    CANCEL
                  </button>

                  <button
                    type="button"
                    className={styles.bulkContinueButton}
                    onClick={() => {
                      setShowBulkApproveModal(false);
                      handleBulkApprove();
                    }}
                  >
                    ✓ CONTINUE TO APPROVE ({selectedShifts.length})
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pagination */}
          {filteredShifts.length > 0 && <PaginationControls />}
        </div>
      </DashboardLayout>{" "}
    </>
  );
};

export default ShiftJobsTable;
